import React, { useEffect, useState, useRef } from "react";
import './MyProfile.css'
import './MyProfile-responsive.css'

import { Ripple } from "primereact/ripple";
import { Image } from 'primereact/image';
import { Toast } from 'primereact/toast';
import { Button } from "primereact/button";

import TextInput from "../../Components/TextInput";
import ErrorPage from "../../Components/ErrorPage";
import { imageFileExtension } from "../../Api/FileConstant";
import { goToTop } from "../../Components/goToTop";

import { updateCompanyDetailForProfileApi, updateProfileImageApi, updateUserProfileApi, userProfile } from "../../Api/UserData";
import { checkEmail, checkPhoneNumber, checkText } from "../../Utils/Validator";
import { useSelector } from "react-redux";
import { RootState } from "../../Controllers/Store";
import { hasPermission } from "../../Controllers/Selectors/permissionsSelector";
import PageLoader from "../../Components/PageLoader";
import GoBackButton from "../../Components/GoBackButton";

const MyProfile: React.FC = () => {
    const toast = useRef<Toast>(null);
    const USERID: string = localStorage.getItem('user_id') || "";
    const defaultProfileImage = '/assets/images/no_data/no_profile_img.svg';

    const [loading, setLoading] = useState<boolean>(false);
    const [tab, setTab] = useState<string>('personal');
    const [editState, setEditState] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [dataLoading, setDataLoading] = useState<boolean>(false);
    const [userData, setUserData] = useState<any>([]);
    const [companyData, setCompanyData] = useState<any>([]);
    const [personalInfoUpdateHasChanges, setPersonalInfoUpdateHasChanges] = useState<boolean>(false);
    const [companyInfoUpdateHasChanges, setCompanyInfoUpdateHasChanges] = useState<boolean>(false);
    const [countryCode, setCountryCode] = useState<string>('')

    const [profileImage, setProfileImage] = useState<string | null>(null);
    const [profileImageError, setProfileImageError] = useState<string>('')

    /* Personal info */
    const [firstName, setFirstName] = useState<string>('')
    const [lastName, setLastName] = useState<string>('')
    const [phone, setPhone] = useState<string>('')
    const [email, setEmail] = useState<string>('')
    const [country, setCountry] = useState<string>('')
    const [addressLine1, setAddressLine1] = useState<string>('')
    const [addressLine2, setAddressLine2] = useState<string>('')
    const [city, setCity] = useState<string>('')
    const [postCode, setPostCode] = useState<string>('')

    /* Company info */
    const [companyName, setCompanyName] = useState<string>('')
    const [companyRegNo, setCompanyRegNo] = useState<string>('')
    const [companyPhone, setCompanyPhone] = useState<string>('')
    const [companyEmail, setCompanyEmail] = useState<string>('')
    const [companyAddress, setCompanyAddress] = useState<string>('')
    const [companyCity, setCompanyCity] = useState<string>('')
    const [companyPostCode, setCompanyPostCode] = useState<string>('')

    /* Errors */
    const [firstNameError, setFirstNameError] = useState<string>('')
    const [lastNameError, setLastNameError] = useState<string>('')
    const [phoneError, setPhoneError] = useState<string>('')
    const [emailError, setEmailError] = useState<string>('')
    const [addressLine1Error, setAddressLine1Error] = useState<string>('')
    const [addressLine2Error, setAddressLine2Error] = useState<string>('')
    const [cityError, setCityError] = useState<string>('')
    const [postCodeError, setPostCodeError] = useState<string>('')

    const [companyNameError, setCompanyNameError] = useState<string>('')
    const [companyRegNoError, setCompanyRegNoError] = useState<string>('')
    const [companyPhoneError, setCompanyPhoneError] = useState<string>('')
    const [companyEmailError, setCompanyEmailError] = useState<string>('')
    const [companyAddressError, setCompanyAddressError] = useState<string>('')
    const [companyCityError, setCompanyCityError] = useState<string>('')
    const [companyPostCodeError, setCompanyPostCodeError] = useState<string>('')

    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) => hasPermission(state, screen, permission));
    };

    const hasPermissionCompanyDetails = useCanEdit('Site Menu', 'Company Details');

    useEffect(() => {
        profileDataGet();
    }, [])

    const profileDataGet = async () => {
        try {
            const userId = localStorage.getItem("user_id");
            const token = localStorage.getItem("token");

            if (!userId || !token) {
                setError("Authentication failed. Please login again.");
                return;
            }

            setDataLoading(true);

            const data = await userProfile(token);

            if (!data?.status) {
                setError("Something went wrong while fetching profile.");
                return;
            }

            setUserData(data.userData ?? null);
            setCompanyData(data.companyData ?? null);

        } catch (error) {
            console.error("Profile fetch error:", error);
            setError("Unexpected error occurred.");
        } finally {
            setDataLoading(false);
        }
    };

    const handleSetUserData = async () => {
        if (!userData) return;

        const countryData = userData?.country_data;

        // Country Code
        const countryCode = countryData?.country_code ?? '';
        setCountryCode(countryCode);

        let cleanPhone = userData?.phone ?? '';

        if (countryCode && cleanPhone.startsWith(countryCode)) {
            cleanPhone = cleanPhone.slice(countryCode.length);
        }

        if (countryCode && cleanPhone.startsWith(`+${countryCode}`)) {
            cleanPhone = cleanPhone.slice(countryCode.length + 1);
        }

        setProfileImage(userData.profile_image);
        setFirstName(userData.first_name);
        setLastName(userData.last_name);
        setPhone(cleanPhone);
        setCountry(countryData?.country_name ?? '');
        setEmail(userData.email);
        setAddressLine1(userData.address_1);
        setAddressLine2(userData.address_2);
        setCity(userData.city);
        setPostCode(userData.post_code);

        setCompanyName(companyData.company_name);
        setCompanyRegNo(companyData.company_regno);
        setCompanyPhone(companyData.company_phone);
        setCompanyEmail(companyData.company_email);
        setCompanyAddress(companyData.company_address);
        setCompanyPostCode(companyData.post_code);
        setCompanyCity(companyData.city);
    }

    useEffect(() => {
        handleSetUserData();
    }, [userData, companyData])

    const switchTabs = (tab: React.SetStateAction<string>) => {
        setTab(tab);
    };

    const toggleEdit = () => {
        setEditState(!editState);
    };

    /* ************************************************************ Personal info */
    // Check personal info update has changes
    useEffect(() => {
        if (!userData) {
            setPersonalInfoUpdateHasChanges(false);
            return;
        }

        let originalPhone = userData?.phone ?? '';

        if (countryCode && originalPhone.startsWith(`+${countryCode}`)) {
            originalPhone = originalPhone.slice(countryCode.length + 1);
        } else if (countryCode && originalPhone.startsWith(countryCode)) {
            originalPhone = originalPhone.slice(countryCode.length);
        }

        const hasChanges =
            firstName !== userData?.first_name ||
            lastName !== userData?.last_name ||
            phone !== originalPhone ||
            (addressLine1 ?? "") !== (userData?.address_1 ?? "") ||
            (addressLine2 ?? "") !== (userData?.address_2 ?? "") ||
            (city ?? "") !== (userData?.city ?? "") ||
            (postCode ?? "") !== (userData?.post_code ?? "");

        setPersonalInfoUpdateHasChanges(hasChanges);
    }, [
        userData,
        firstName,
        lastName,
        countryCode,
        phone,
        addressLine1,
        addressLine2,
        city,
        postCode
    ]);

    // Show profile image update API validation
    function showAPIErrorForProfileImage(keyName: string, error: string): void {
        if (keyName === "profile") {
            setProfileImageError(error);
        }
    }

    // Profile image update API
    const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;

        try {
            const response = await updateProfileImageApi(Number(USERID), e.target.files[0]);

            if (response.status) {
                profileDataGet();

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Profile image updated successfully.",
                    life: 3000,
                });

                setEditState(false);
                goToTop();
            } else if (response.statusCode === 422) {
                Object.entries(response.errors ?? []).map(([key, value], index) =>
                    showAPIErrorForProfileImage(key, value[0])
                );
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Profile image updated failed',
                    detail: 'There was an error updating profile image.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error updating profile image:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while updating profile image.",
                life: 3000,
            });
        }
    };

    // Clear error messages
    useEffect(() => {
        checkText(firstName, false, setFirstNameError);
    }, [firstName]);

    useEffect(() => {
        checkText(lastName, false, setLastNameError);
    }, [lastName]);

    useEffect(() => {
        checkPhoneNumber(phone, false, setPhoneError);
    }, [phone]);

    useEffect(() => {
        checkText(addressLine1, false, setAddressLine1Error);
    }, [addressLine1]);

    useEffect(() => {
        checkText(addressLine2, false, setAddressLine2Error);
    }, [addressLine2]);

    useEffect(() => {
        checkText(city, false, setCityError);
    }, [city]);

    useEffect(() => {
        checkText(postCode, false, setPostCodeError);
    }, [postCode]);

    // Check personal info validation
    function checkProfileInfoValidation(): boolean {
        const isValidFirstName = checkText(firstName, true, setFirstNameError);
        const isValidLastName = checkText(lastName, true, setLastNameError);
        const isValidPhoneNumber = checkPhoneNumber(phone, true, setPhoneError);
        const isValidAddressLine1 = checkText(addressLine1, false, setAddressLine1Error);
        const isValidAddressLine2 = checkText(addressLine2, false, setAddressLine2Error);
        const isValidCity = checkText(city, false, setCityError);
        const isValidPostCode = checkText(postCode, false, setPostCodeError);

        return (
            isValidFirstName &&
            isValidLastName &&
            isValidPhoneNumber &&
            isValidAddressLine1 &&
            isValidAddressLine2 &&
            isValidCity &&
            isValidPostCode
        );
    }

    // Show personal info API validation
    function showAPIErrorForPersonalInfo(keyName: string, error: string): void {
        if (keyName === "first_name") {
            setFirstNameError(error);
        }

        if (keyName === "last_name") {
            setLastNameError(error);
        }

        if (keyName === "phone") {
            setPhoneError(error);
        }

        if (keyName === "address_1") {
            setAddressLine1Error(error);
        }

        if (keyName === "address_2") {
            setAddressLine2Error(error);
        }

        if (keyName === "city") {
            setCityError(error);
        }

        if (keyName === "post_code") {
            setPostCodeError(error);
        }
    }

    // Update profile personal info API 
    const handleUpdatePersonalDetails = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkProfileInfoValidation() || !USERID) return;

        setLoading(true);

        try {
            const fullPhoneNumber = (countryCode ?? '') + phone;

            const response = await updateUserProfileApi(
                Number(USERID),
                firstName,
                lastName,
                fullPhoneNumber,
                addressLine1,
                addressLine2,
                city,
                postCode
            );

            if (response.status) {
                profileDataGet();

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Personal details updated successfully.",
                    life: 3000,
                });

                setEditState(false);
                goToTop();
            } else if (response.statusCode === 422) {
                Object.entries(response.errors ?? []).map(([key, value], index) =>
                    showAPIErrorForPersonalInfo(key, value[0])
                );
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error updating personal details.',
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error updating personal details:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while updating personal details.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }
    /* ************************************************************ */


    /* ************************************************************ Company info */
    // Check company info update has changes
    useEffect(() => {
        if (!companyData) {
            setCompanyInfoUpdateHasChanges(false);
            return;
        }

        const hasChanges =
            (companyName ?? "") !== (companyData?.company_name ?? "") ||
            (companyRegNo ?? "") !== (companyData?.company_regno ?? "") ||
            (companyEmail ?? "") !== (companyData?.company_email ?? "") ||
            (companyPhone ?? "") !== (companyData?.company_phone ?? "") ||
            (companyAddress ?? "") !== (companyData?.company_address ?? "") ||
            (companyCity ?? "") !== (companyData?.city ?? "") ||
            (companyPostCode ?? "") !== (companyData?.post_code ?? "");

        setCompanyInfoUpdateHasChanges(hasChanges);
    }, [
        companyData,
        companyName,
        companyRegNo,
        companyEmail,
        companyPhone,
        companyAddress,
        companyCity,
        companyPostCode
    ]);

    // Clear errors
    useEffect(() => {
        checkText(companyName, false, setCompanyNameError);
    }, [companyName]);

    useEffect(() => {
        checkText(companyRegNo, false, setCompanyRegNoError);
    }, [companyRegNo]);

    useEffect(() => {
        checkPhoneNumber(companyPhone, false, setCompanyPhoneError);
    }, [companyPhone]);

    useEffect(() => {
        checkEmail(companyEmail, false, setCompanyEmailError);
    }, [companyEmail]);

    useEffect(() => {
        checkText(companyAddress, false, setCompanyAddressError);
    }, [companyAddress]);

    useEffect(() => {
        checkText(companyCity, false, setCompanyCityError);
    }, [companyCity]);

    useEffect(() => {
        checkText(companyPostCode, false, setCompanyPostCodeError);
    }, [companyPostCode]);

    // Check company info validation
    function checkCompanyInfoValidation(): boolean {
        const isValidCompanyName = checkText(companyName, false, setCompanyNameError);
        const isValidCompanyRegNo = checkText(companyRegNo, false, setCompanyRegNoError);
        const isValidCompanyPhone = checkPhoneNumber(companyPhone, false, setCompanyPhoneError);
        const isValidCompanyEmail = checkEmail(companyEmail, false, setCompanyEmailError);
        const isValidCompanyAddress = checkText(companyAddress, false, setCompanyAddressError);
        const isValidCompanyCity = checkText(companyCity, false, setCompanyCityError);
        const isValidCompanyPostCode = checkText(companyPostCode, false, setCompanyPostCodeError);

        return (
            isValidCompanyName &&
            isValidCompanyRegNo &&
            isValidCompanyPhone &&
            isValidCompanyEmail &&
            isValidCompanyAddress &&
            isValidCompanyCity &&
            isValidCompanyPostCode
        );
    }

    // Show company info API validation
    function showAPIErrorForCompanyInfo(keyName: string, error: string): void {
        if (keyName === "company_name") {
            setCompanyNameError(error);
        }

        if (keyName === "company_regno") {
            setCompanyRegNoError(error);
        }

        if (keyName === "company_email") {
            setCompanyEmailError(error);
        }

        if (keyName === "company_phone") {
            setCompanyPhoneError(error);
        }

        if (keyName === "company_address") {
            setCompanyAddressError(error);
        }

        if (keyName === "post_code") {
            setCompanyPostCodeError(error);
        }

        if (keyName === "city") {
            setCompanyCityError(error);
        }
    }

    // Update company info API
    const handleUpdateCompanyDetails = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!checkCompanyInfoValidation() || !companyData) return;

        setLoading(true);

        try {
            const response = await updateCompanyDetailForProfileApi(
                companyData?.company_id,
                companyName,
                companyRegNo,
                companyEmail,
                companyPhone,
                companyAddress,
                companyPostCode,
                companyCity
            );

            if (response.status) {
                profileDataGet();

                toast?.current?.show({
                    severity: "success",
                    summary: "Success",
                    detail: "Company details updated successfully.",
                    life: 3000,
                });

                setEditState(false);
                goToTop();
            } else if (response.statusCode === 422) {
                Object.entries(response.errors ?? []).map(([key, value], index) =>
                    showAPIErrorForCompanyInfo(key, value[0])
                );
            } else {
                toast?.current?.show({
                    severity: 'error',
                    summary: 'Failed',
                    detail: 'There was an error updating company details',
                    life: 3000
                });
            }
        } catch (error) {
            console.error("Error updating company details:", error);
            toast.current?.show({
                severity: "error",
                summary: "Error",
                detail: "Something went wrong while updating company details.",
                life: 3000,
            });
        } finally {
            setLoading(false);
        }
    }
    /* ************************************************************ */

    return (
        <React.Fragment>

            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {dataLoading ? (
                    <PageLoader />
                ) : error ? (
                    <ErrorPage errorMessage={error} />
                ) : (
                    <React.Fragment>
                        <div className="main_head_area">
                            <h4 className="main_heading">My profile</h4>

                            <GoBackButton />
                        </div>
                        <div className="profile_page_area">
                            <div className="profile_page_head">
                                <h4>
                                    {tab === 'personal' ? (
                                        <React.Fragment>{editState ? 'Edit personal info' : 'My Details'}</React.Fragment>
                                    ) : tab === 'company' ? (
                                        <React.Fragment>{editState ? 'Edit company info' : 'Company Details'}</React.Fragment>
                                    ) : null}
                                </h4>
                                <button className="profile_edit_btn p-ripple" onClick={toggleEdit}>
                                    <i className={`bi ${editState ? 'bi-x-lg text-danger' : 'bi-pencil-square'}`}></i>
                                    <Ripple />
                                </button>
                            </div>

                            <div className="profile_page_body">
                                <div className="row">
                                    <div className="col-12 col-xl-4 mx-auto page_divider custom_page_height">
                                        <div className="profile_view_area mt-1 mt-sm-1 mb-3 mb-sm-0 pt-sm-2">
                                            {editState && tab === 'personal' ? (
                                                <React.Fragment>
                                                    <div className="form_img_upload_area pt-0 pb-0">
                                                        <div className="form_img_upload">
                                                            <input
                                                                type="file"
                                                                accept={imageFileExtension}
                                                                className="form_img_upload_input"
                                                                id="profileImage"
                                                                onChange={handleImageChange}
                                                            />
                                                            <div className="form_img_upload_label_area">
                                                                <label
                                                                    htmlFor="profileImage"
                                                                    className={`form_img_upload_label ${profileImage ? 'uploaded' : ''}`}
                                                                    style={{ backgroundImage: profileImage ? `url(${profileImage})` : `` }}
                                                                >
                                                                    {!profileImage && (
                                                                        <div className="upload_icon">
                                                                            <i className="bi bi-camera-fill"></i>
                                                                        </div>
                                                                    )}
                                                                </label>

                                                                <label htmlFor="profileImage" className="upload_update_icon">
                                                                    <i className="bi bi-camera-fill"></i>
                                                                </label>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {profileImageError &&
                                                        <small className="form_error_msg text-center mt-2">{profileImageError}</small>
                                                    }
                                                </React.Fragment>
                                            ) : (
                                                <div className="profile_view_main_area">
                                                    {profileImage ? (
                                                        <Image
                                                            className="profile_img_area"
                                                            src={profileImage}
                                                            alt=""
                                                            preview
                                                        />
                                                    ) : (
                                                        <Image
                                                            className="profile_img_area p-sm-2 p-1"
                                                            src={defaultProfileImage}
                                                            alt=""
                                                        />
                                                    )}

                                                    {(userData?.first_name || userData?.last_name) && (
                                                        <h5 className="profile_name">
                                                            <React.Fragment>
                                                                {(userData?.first_name ? userData?.first_name : '') + (userData?.last_name ? (' ' + userData?.last_name) : '')}
                                                            </React.Fragment>
                                                        </h5>
                                                    )}

                                                    <h6 className="profile_role">
                                                        {userData?.type_name}
                                                    </h6>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="col-12 col-xl-8">
                                        <div className="profile_view_area pt-3 mt-0 pb-0">
                                            <div className="profile_edit_tabs" id="profileEditTab" role="tablist" aria-orientation="vertical">
                                                <button
                                                    className={`profile_view_tab_btn p-ripple ${tab === 'personal' ? 'active' : ''}`}
                                                    onClick={() => switchTabs('personal')}
                                                    id="personal-detail-tab"
                                                    data-bs-toggle="pill"
                                                    data-bs-target="#personal-detail"
                                                    type="button"
                                                    role="tab"
                                                    aria-controls="personal-detail"
                                                    aria-selected={tab === 'personal'}
                                                    disabled={editState ? true : false}
                                                >
                                                    Personal info
                                                    <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                                                </button>

                                                {hasPermissionCompanyDetails && (
                                                    <button
                                                        className={`profile_view_tab_btn p-ripple ${tab === 'company' ? 'active' : ''}`}
                                                        onClick={() => switchTabs('company')}
                                                        id="company-detail-tab"
                                                        data-bs-toggle="pill"
                                                        data-bs-target="#company-detail"
                                                        type="button"
                                                        role="tab"
                                                        aria-controls="company-detail"
                                                        aria-selected={tab === 'company'}
                                                        disabled={editState ? true : false}
                                                    >
                                                        Company info
                                                        <Ripple pt={{ root: { style: { background: 'rgba(164, 96, 253, 0.2)' } } }} />
                                                    </button>
                                                )}
                                            </div>

                                            <div className="tab-content profile_view_tab_content" id="profileEditTabContent">
                                                {/* Personal Details */}
                                                <div className={`tab-pane profile_view_tab_panel fade ${tab === 'personal' ? 'show active' : ''}`} id="personal-detail" role="tabpanel" aria-labelledby="personal-detail-tab" tabIndex={0}>
                                                    {editState ? (
                                                        <div className="row">
                                                            {/* First name */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <TextInput
                                                                    id="firstName"
                                                                    label="First name"
                                                                    labelHtmlFor="firstName"
                                                                    required={true}
                                                                    value={firstName}
                                                                    placeholder="First name"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFirstName(e.target.value)}
                                                                    error={firstNameError}
                                                                />
                                                            </div>

                                                            {/* Last name */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <TextInput
                                                                    id="lastName"
                                                                    label="Last name"
                                                                    labelHtmlFor="lastName"
                                                                    required={true}
                                                                    value={lastName}
                                                                    placeholder="Last name"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLastName(e.target.value)}
                                                                    error={lastNameError}
                                                                />
                                                            </div>

                                                            {/* Phone */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <TextInput
                                                                    id="phone"
                                                                    label="Phone"
                                                                    labelHtmlFor="phone"
                                                                    inputType="number"
                                                                    required={true}
                                                                    value={phone}
                                                                    placeholder="Phone number"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPhone(e.target.value)}
                                                                    error={phoneError}
                                                                    prefix={countryCode}
                                                                />
                                                            </div>

                                                            {/* Email */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <TextInput
                                                                    id="email"
                                                                    label="Email"
                                                                    subLabel=" (Cannot be changed)"
                                                                    labelHtmlFor="email"
                                                                    value={email}
                                                                    placeholder="Email"
                                                                    inputDisabled={true}
                                                                />
                                                            </div>

                                                            {/* Country */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <TextInput
                                                                    id="country"
                                                                    label="Country"
                                                                    subLabel=" (Cannot be changed)"
                                                                    labelHtmlFor="country"
                                                                    value={country}
                                                                    placeholder="Country"
                                                                    inputDisabled={true}
                                                                />
                                                            </div>

                                                            {/* Address line 1 */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <TextInput
                                                                    id="addressLine1"
                                                                    label="Address line 1"
                                                                    labelHtmlFor="addressLine1"
                                                                    required={false}
                                                                    value={addressLine1}
                                                                    placeholder="Address line 1"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddressLine1(e.target.value)}
                                                                    error={addressLine1Error}
                                                                />
                                                            </div>

                                                            {/* Address line 2 */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <TextInput
                                                                    id="addressLine2"
                                                                    label="Address line 2"
                                                                    labelHtmlFor="addressLine2"
                                                                    required={false}
                                                                    value={addressLine2}
                                                                    placeholder="Address line 2"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddressLine2(e.target.value)}
                                                                    error={addressLine2Error}
                                                                />
                                                            </div>

                                                            {/* City */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <TextInput
                                                                    id="city"
                                                                    label="City"
                                                                    labelHtmlFor="city"
                                                                    required={false}
                                                                    value={city}
                                                                    placeholder="City"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCity(e.target.value)}
                                                                    error={cityError}
                                                                />
                                                            </div>

                                                            {/* Post code */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <TextInput
                                                                    id="postCode"
                                                                    label="Post code"
                                                                    labelHtmlFor="postCode"
                                                                    required={false}
                                                                    value={postCode}
                                                                    placeholder="Post code"
                                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPostCode(e.target.value)}
                                                                    error={postCodeError}
                                                                />
                                                            </div>

                                                            <div className="col-12">
                                                                <div className="text-end mt-3">
                                                                    <Button
                                                                        label={`${loading ? 'Updating' : 'Update'}`}
                                                                        onClick={handleUpdatePersonalDetails}
                                                                        loading={loading}
                                                                        className="custom_btn primary"
                                                                        disabled={!personalInfoUpdateHasChanges}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="row">
                                                            {/* First name */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">First name</label>
                                                                    <div className="form_data_view_box">
                                                                        {userData?.first_name ? (
                                                                            <p>{userData.first_name}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text text_italic">N/A</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Last name */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">Last name</label>
                                                                    <div className="form_data_view_box">
                                                                        {userData?.last_name ? (
                                                                            <p>{userData.last_name}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text text_italic">N/A</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Phone */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">Phone</label>
                                                                    <div className="form_data_view_box">
                                                                        {userData?.phone ? (
                                                                            <p>{userData.phone}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text text_italic">N/A</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Email */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">Email</label>
                                                                    <div className="form_data_view_box">
                                                                        {userData?.email ? (
                                                                            <p>{userData.email}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text text_italic">N/A</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Country */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">Country</label>
                                                                    <div className="form_data_view_box">
                                                                        {userData?.country_data?.country_name ? (
                                                                            <p>{userData?.country_data?.country_name}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text text_italic">N/A</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Address line 1 */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">Address line 1</label>
                                                                    <div className="form_data_view_box">
                                                                        {userData?.address_1 ? (
                                                                            <p>{userData.address_1}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text text_italic">N/A</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Address line 2 */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">Address line 2</label>
                                                                    <div className="form_data_view_box">
                                                                        {userData?.address_2 ? (
                                                                            <p>{userData.address_2}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text text_italic">N/A</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* City */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">City</label>
                                                                    <div className="form_data_view_box">
                                                                        {userData?.city ? (
                                                                            <p>{userData.city}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text text_italic">N/A</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>

                                                            {/* Post code */}
                                                            <div className="col-12 col-xl-6 col-sm-6">
                                                                <div className="page_form_group">
                                                                    <label className="custom_form_label">Post code</label>
                                                                    <div className="form_data_view_box">
                                                                        {userData?.post_code ? (
                                                                            <p>{userData.post_code}</p>
                                                                        ) : (
                                                                            <span className="placeholder_text text_italic">N/A</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Company Details */}
                                                {hasPermissionCompanyDetails && (
                                                    <div className={`tab-pane profile_view_tab_panel fade ${tab === 'company' ? 'show active' : ''}`} id="company-detail" role="tabpanel" aria-labelledby="company-detail-tab" tabIndex={0}>
                                                        {editState ? (
                                                            <div className="row">
                                                                {/* Company name */}
                                                                <div className="col-12 col-xl-6 col-sm-6">
                                                                    <TextInput
                                                                        id="companyName"
                                                                        label="Company name"
                                                                        labelHtmlFor="companyName"
                                                                        required={false}
                                                                        value={companyName}
                                                                        placeholder="Company name"
                                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyName(e.target.value)}
                                                                        error={companyNameError}
                                                                    />
                                                                </div>

                                                                {/* Company reg no */}
                                                                <div className="col-12 col-xl-6 col-sm-6">
                                                                    <TextInput
                                                                        id="companyRegNo"
                                                                        label="Company reg no"
                                                                        labelHtmlFor="companyRegNo"
                                                                        required={false}
                                                                        value={companyRegNo}
                                                                        placeholder="Company reg no"
                                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyRegNo(e.target.value)}
                                                                        error={companyRegNoError}
                                                                    />
                                                                </div>

                                                                {/* Phone */}
                                                                <div className="col-12 col-xl-6 col-sm-6">
                                                                    <TextInput
                                                                        id="companyPhone"
                                                                        label="Phone"
                                                                        labelHtmlFor="companyPhone"
                                                                        required={false}
                                                                        value={companyPhone}
                                                                        placeholder="Phone"
                                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyPhone(e.target.value)}
                                                                        error={companyPhoneError}
                                                                    />
                                                                </div>

                                                                {/* Email */}
                                                                <div className="col-12 col-xl-6 col-sm-6">
                                                                    <TextInput
                                                                        id="companyEmail"
                                                                        label="Email"
                                                                        labelHtmlFor="companyEmail"
                                                                        required={false}
                                                                        value={companyEmail}
                                                                        placeholder="Email"
                                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyEmail(e.target.value)}
                                                                        error={companyEmailError}
                                                                        keyFilter={'email'}
                                                                    />
                                                                </div>

                                                                {/* Address */}
                                                                <div className="col-12">
                                                                    <TextInput
                                                                        id="companyAddress"
                                                                        label="Address"
                                                                        labelHtmlFor="companyAddress"
                                                                        required={false}
                                                                        value={companyAddress}
                                                                        placeholder="Address"
                                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyAddress(e.target.value)}
                                                                        error={companyAddressError}
                                                                    />
                                                                </div>

                                                                {/* City */}
                                                                <div className="col-12 col-xl-6 col-sm-6">
                                                                    <TextInput
                                                                        id="companyCity"
                                                                        label="City"
                                                                        labelHtmlFor="companyCity"
                                                                        required={false}
                                                                        value={companyCity}
                                                                        placeholder="City"
                                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyCity(e.target.value)}
                                                                        error={companyCityError}
                                                                    />
                                                                </div>

                                                                {/* Post code */}
                                                                <div className="col-12 col-xl-6 col-sm-6">
                                                                    <TextInput
                                                                        id="companyPostCode"
                                                                        label="Post code"
                                                                        labelHtmlFor="companyPostCode"
                                                                        required={false}
                                                                        value={companyPostCode}
                                                                        placeholder="Post code"
                                                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCompanyPostCode(e.target.value)}
                                                                        error={companyPostCodeError}
                                                                    />
                                                                </div>

                                                                <div className="col-12">
                                                                    <div className="text-end mt-3">
                                                                        <Button
                                                                            label={`${loading ? 'Updating' : 'Update'}`}
                                                                            onClick={handleUpdateCompanyDetails}
                                                                            loading={loading}
                                                                            className="custom_btn primary"
                                                                            disabled={!companyInfoUpdateHasChanges}
                                                                        />
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="row">
                                                                {/* Company name */}
                                                                <div className="col-12 col-xl-6 col-sm-6">
                                                                    <div className="page_form_group">
                                                                        <label className="custom_form_label">Company name</label>
                                                                        <div className="form_data_view_box">
                                                                            {companyData?.company_name ? (
                                                                                <p>{companyData.company_name}</p>
                                                                            ) : (
                                                                                <span className="placeholder_text text_italic">N/A</span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Company reg no */}
                                                                <div className="col-12 col-xl-6 col-sm-6">
                                                                    <div className="page_form_group">
                                                                        <label className="custom_form_label">Company reg. no.</label>
                                                                        <div className="form_data_view_box">
                                                                            {companyData?.company_regno ? (
                                                                                <p>{companyData.company_regno}</p>
                                                                            ) : (
                                                                                <span className="placeholder_text text_italic">N/A</span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Phone */}
                                                                <div className="col-12 col-xl-6 col-sm-6">
                                                                    <div className="page_form_group">
                                                                        <label className="custom_form_label">Phone</label>
                                                                        <div className="form_data_view_box">
                                                                            {companyData?.company_phone ? (
                                                                                <p>{companyData.company_phone}</p>
                                                                            ) : (
                                                                                <span className="placeholder_text text_italic">N/A</span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Email */}
                                                                <div className="col-12 col-xl-6 col-sm-6">
                                                                    <div className="page_form_group">
                                                                        <label className="custom_form_label">Email</label>
                                                                        <div className="form_data_view_box">
                                                                            {companyData?.company_email ? (
                                                                                <p>{companyData.company_email}</p>
                                                                            ) : (
                                                                                <span className="placeholder_text text_italic">N/A</span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Address */}
                                                                <div className="col-12">
                                                                    <div className="page_form_group">
                                                                        <label className="custom_form_label">Address</label>
                                                                        <div className="form_data_view_box">
                                                                            {companyData?.company_address ? (
                                                                                <p>{companyData.company_address}</p>
                                                                            ) : (
                                                                                <span className="placeholder_text text_italic">N/A</span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* City */}
                                                                <div className="col-12 col-xl-6 col-sm-6">
                                                                    <div className="page_form_group">
                                                                        <label className="custom_form_label">City</label>
                                                                        <div className="form_data_view_box">
                                                                            {companyData?.city ? (
                                                                                <p>{companyData.city}</p>
                                                                            ) : (
                                                                                <span className="placeholder_text text_italic">N/A</span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Post code */}
                                                                <div className="col-12 col-xl-6 col-sm-6">
                                                                    <div className="page_form_group">
                                                                        <label className="custom_form_label">Post code</label>
                                                                        <div className="form_data_view_box">
                                                                            {companyData?.post_code ? (
                                                                                <p>{companyData.post_code}</p>
                                                                            ) : (
                                                                                <span className="placeholder_text text_italic">N/A</span>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </React.Fragment>
                )}
            </div>
        </React.Fragment>
    )
};

export default MyProfile;